import React, { useContext, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import moment, { Moment } from 'moment'
import Pane from 'components/Common/Pane'
import config from 'constants/config'
import isTimeLimited from 'lib/offer/isTimeLimited'
import OfferTimeLimitedText from '../OfferTimeLimitedText'
import LinePlaneFlyIcon from 'components/Luxkit/Icons/line/LinePlaneFlyIcon'
import LineGiftIcon from 'components/Luxkit/Icons/line/LineGiftIcon'
import OfferPartnershipLabel from 'components/Common/Labels/OfferPartnershipLabel'
import PriceRowTaxesAndFees from 'components/Luxkit/PricePoints/PriceRowTaxesAndFees'
import { OFFER_TYPE_HOTEL_SLUG } from 'constants/offer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import AdditionalGuestsPopup from 'components/Common/AdditionalGuestsPopup/AdditionalGuestsPopup'
import { OfferPackageOptionView, getCheapestSelectedPackageOptionWithDefault, arePricesLoading, isBNBLAvailable, getFromDurationLabel, isAvailableForPurchase } from 'selectors/offerPage/offerPageSelectors'
import { includedGuestsExceededItems } from 'lib/checkout/cartReservationUtils'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import TextButton from 'components/Luxkit/Button/TextButton'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import TextLink from 'components/Luxkit/TextLink'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import Caption from 'components/Luxkit/Typography/Caption'
import BodyText from 'components/Luxkit/Typography/BodyText'
import AtolProtectedTooltip from 'components/OfferPage/AtolProtectedTooltip'
import GeoContext from 'contexts/geoContext'
import { isDepositAvailableOnOffer } from 'lib/payment/depositsUtils'
import SecureWithDepositTagAndTooltip from 'components/Common/SecureWithDeposit/SecureWithDepositTagAndTooltip'
import ReserveForZeroTag from 'components/Common/ReserveForZeroTagAndTooltip/ReserveForZeroTag'
import { isPackageEligibleForZeroReservation } from 'lib/offer/reserveForZeroUtils'
import CaptionTextBlock from 'components/Luxkit/TextBlocks/CaptionTextBlock'
import Group from 'components/utils/Group'
import BusinessTravellerOfferCreditsTextLink from 'businessTraveller/components/offer-credits/BusinessTravellerOfferCreditsTextLink'
import PriceRowPriceCaption from 'components/Luxkit/PricePoints/PriceRowPriceCaption'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import PriceRowValueDiscountWithCaption from 'components/Luxkit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import Tooltip from 'components/Luxkit/Tooltip'
import getGiftingMessage from 'lib/gifting/getGiftingMessage'
import PriceRowFlightPackage from 'components/Luxkit/PricePoints/PriceRowFlightPackage'
import PriceRowCaption from 'components/Luxkit/PricePoints/PriceRowCaption'
import { useAppDispatch } from 'hooks/reduxHooks'
import { setCustomerValue } from 'actions/AuthActions'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import { unique } from 'lib/array/arrayUtils'
import OfferPriceDetailsRow from 'components/Common/PriceDetails/OfferPriceDetailsRow'
import { feesToObject, priceWithoutPropertyFees } from 'lib/offer/pricing'
import LoyaltyPoints from 'loyalty/components/LoyaltyPoints'
import WalledContent from 'components/Common/WalledContent'
import { generateLoyaltyPointsCalculatorHotelOptions, generateLoyaltyPointsCalculatorBundledFlightOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import SoftOfferPageWalledGarden from 'components/Common/SoftOfferPageWalledGarden'

const StyledOfferPartnershipLabel = styled(OfferPartnershipLabel)`
  margin-top: ${rem(12)};
`

const SummaryPane = styled(Pane)`
  position: relative;

  > * + * {
    margin-top: ${rem(12)};
  }
`

const PackagePrices = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: ${rem(4)};
  align-items: center;
`

const ChangeDepartureWrapper = styled.div`
  margin-bottom: auto;
  margin-top: ${rem(8)};
  text-align: center;
`

interface Props {
  offer: App.Offer;
  flightOrigin?: App.Airport;
  flightDestination?: App.Airport;
  rooms: Array<App.Occupants>;
  className?: string;
  cheapestSelectedOption: OfferPackageOptionView;
  onViewPackageOptions: () => void;
  calendarsLoading?: boolean;
  checkIn?: string;
  isBNBLAvailable: boolean;
  isSpoofed: boolean;
  durationLabel: string;
  availableForPurchase?: boolean;
}

function FlashOfferSummary(props: Props) {
  const {
    className,
    onViewPackageOptions,
    offer,
    flightOrigin,
    cheapestSelectedOption: packageView,
    rooms,
    flightDestination,
    calendarsLoading,
    checkIn,
    isBNBLAvailable,
    isSpoofed,
    durationLabel,
    availableForPurchase,
  } = props
  const { currentCurrency, currentRegionCode } = useContext(GeoContext)
  const dispatch = useOfferPageDispatch()
  const appDispatch = useAppDispatch()

  const {
    flightPrice,
    package: pkg,
    pricing: hotelPricing,
    memberPricing: hotelMemberPricing,
    pricingWithFlights,
    memberPricingWithFlights,
  } = packageView

  const showPrices = packageView.capacityValid && packageView.hotelAvailable
  const flightsEnabled = flightOrigin && offer.offerFlightsEnabled && config.businessTraveller.currentAccountMode !== 'business'
  const hasFlights = !!pricingWithFlights && !offer.flightCacheDisabled
  const showIncludedGuestsExceeded = useMemo(() => !pkg.roomRate?.extraGuestSurcharges.length && includedGuestsExceededItems(rooms || [], pkg, offer), [pkg, rooms, offer])

  const onToggleFilter = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    dispatch({ type: OfferPageActions.toggleFilter, filter: 'airport' })
  }

  const checkInMoment = useMemo(() => checkIn ? moment(checkIn) : undefined, [checkIn])
  const departureTooFarInFuture = useMemo(() => checkInMoment && checkInMoment.diff(moment.utc(), 'days') > 330, [checkInMoment])
  const hideNoCacheMessaging = !hasFlights && offer.bundledWithFlightsOnly

  let pricingToDisplay: App.PricingWithValue | undefined = hotelPricing
  let memberPricingToDisplay: App.PricingWithValue | undefined = hotelMemberPricing
  if (offer.bundledWithFlightsOnly) {
    if (flightsEnabled && !hideNoCacheMessaging && hasFlights) {
      pricingToDisplay = pricingWithFlights
      memberPricingToDisplay = memberPricingWithFlights
    } else {
      pricingToDisplay = undefined
      memberPricingToDisplay = undefined
    }
  }

  const fees = pricingToDisplay ? feesToObject(pricingToDisplay.fees) : {}

  const showMemberPrice = !!memberPricingToDisplay && memberPricingToDisplay.price > 0
  const showDiscountPill = !offer?.isDiscountPillHidden && pricingToDisplay && pricingToDisplay.discountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE

  const showPriceDetails = (fees.taxesAndFees ?? 0) > 0 || (fees.property ?? 0) > 0 || config.agentHub.isEnabled

  const showSecureWithDepositTag = isDepositAvailableOnOffer(offer, currentCurrency, checkInMoment, hotelPricing.price)
  const isReserveForZeroEnabled = config.RESERVE_FOR_ZERO_ENABLED
  const isReserveForZeroTagShown = (isReserveForZeroEnabled || isSpoofed) && isPackageEligibleForZeroReservation(offer, pkg.roomRate?.isReservableForZero, checkInMoment)
  const isDepositTagShown = showSecureWithDepositTag
  const shouldShowValue = !!pricingToDisplay && pricingToDisplay.value > pricingToDisplay.price && pkg.shouldDisplayValue
  const isIndiaRegion = currentRegionCode === 'IN'
  const isMemberOnlyOffer = offer.luxPlus.access === 'memberOnly'
  const showDisclaimers = (flightsEnabled && !hideNoCacheMessaging) || (isBNBLAvailable && !isMemberOnlyOffer)

  let saleUnit = pricingToDisplay?.saleUnit ?? offer.saleUnit
  if (offer.bundledWithFlightsOnly) {
    saleUnit = 'package*'
  }

  useEffect(() => {
    appDispatch(setCustomerValue(hotelPricing.price))
  }, [appDispatch, hotelPricing.price])

  const bookByFormatted = useMemo<Moment | undefined>(() => {
    return offer.bookByDate ? moment(offer.bookByDate) : undefined
  }, [offer.bookByDate])

  const travelUntilFormatted = useMemo<Moment | undefined>(() => {
    return offer.travelToDate ? moment(offer.travelToDate) : undefined
  }, [offer.travelToDate])

  const { partnership } = useContext(GeoContext)

  const viewOptionsLabel = `View ${config.brandConfig.travelSynonym} options`

  const giftingMessage = useMemo<string | undefined>(() => {
    if (bookByFormatted && travelUntilFormatted) {
      return getGiftingMessage({ bookBy: bookByFormatted, travelUntil: travelUntilFormatted })
    }
  }, [bookByFormatted, travelUntilFormatted])

  const offerLocations = useMemo(() => unique(offer.locations.concat(offer.location)),
    [offer.locations, offer.location])
  const vendorName = offer.vendorName

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => [
    generateLoyaltyPointsCalculatorHotelOptions(offer, { packageView }),
    generateLoyaltyPointsCalculatorBundledFlightOptions(offer, {
      packageView,
      skip: !offer.bundledWithFlightsOnly || !hasFlights || !flightsEnabled,
    }),
  ], [offer, packageView, hasFlights, flightsEnabled])

  return (
    <SummaryPane border="neutral-six" borderRadius="XS" padding="28 20" shadow="bottom-small" className={className} data-testid="SummaryPane">
      <Group direction="vertical" gap={16}>
        {availableForPurchase && <WalledContent
          enforced={offer.walledGarden}
          wall={<BodyText variant="large" weight="bold">{offer.durationLabel} packages available</BodyText>}
        >
          {showPrices &&
            <PackagePrices>
              {isTimeLimited(offer) && <OfferTimeLimitedText />}
              {isDepositTagShown && !isSpoofed && <SecureWithDepositTagAndTooltip offerType={offer.type} />}
              {isReserveForZeroTagShown && <ReserveForZeroTag />}
              <Group direction="vertical" horizontalAlign="center" gap={8}>
                <Group direction="vertical" horizontalAlign="center">
                  {offer.bundledWithFlightsOnly &&
                    <CaptionTextBlock variant="large" weight="bold" startIcon={<LinePlaneFlyIcon />}>
                      Hotel + flights{hideNoCacheMessaging && ' packages available'}
                    </CaptionTextBlock>
                  }
                  {pricingToDisplay && <>
                    <PriceRowPriceCaption>{durationLabel}</PriceRowPriceCaption>
                    <LuxPlusPriceStack
                      size="L"
                      price={pricingToDisplay.price}
                      memberPrice={memberPricingToDisplay?.price}
                      saleUnit={saleUnit}
                      testid="pricing"
                      horizontalAlign="center"
                      offerConfig={offer.luxPlus}
                      leadWithLuxPlusPrice
                    />
                    {offer.bundledWithFlightsOnly && !rooms && <Caption variant="medium" colour="neutral-three">
                      &nbsp;Two Adults
                    </Caption>}
                    <PriceRowTaxesAndFees
                      endIcon={showPriceDetails && <OfferPriceDetailsRow
                        offerPage
                        offer={offer}
                        duration={pkg.duration}
                        propertyFees={fees.property}
                        taxesAndFees={fees.taxesAndFees}
                        hotelPrice={priceWithoutPropertyFees(hotelPricing)}
                        hotelMemberPrice={hotelMemberPricing ? priceWithoutPropertyFees(hotelMemberPricing) : undefined}
                        showMemberPrice={showMemberPrice}
                        flightPrice={offer.bundledWithFlightsOnly ? flightPrice : undefined}
                        rooms={rooms}
                        extraGuestMessage={showIncludedGuestsExceeded ? pkg.roomPolicyDescription : ''}
                        dueAtPropertyMessage={offer.property?.taxesAndFeesContent ? offer.property.taxesAndFeesContent : ''}
                        trigger="info-icon"
                        cta={
                          <TextButton
                            kind="primary"
                            fit="flex"
                          >
                            {viewOptionsLabel}
                          </TextButton>
                        }
                        onCtaAfterClose={onViewPackageOptions}
                      />}
                    />
                    {showIncludedGuestsExceeded && (
                      <AdditionalGuestsPopup
                        complex={false}
                        description="+ Extra guest surcharge"
                        modalContent={pkg.roomPolicyDescription}
                      />
                    )}
                    {shouldShowValue && <PriceRowValueDiscountWithCaption
                      data-testid="value-box"
                      size="M"
                      originalValue={pricingToDisplay.value}
                      discountPercentage={showDiscountPill ? pricingToDisplay.discountPercent : undefined}
                    />}
                  </>}
                </Group>
                <LoyaltyPoints calculationRequests={pointsEarnCalculationRequests} calculationType="estimate" />
              </Group>
              { partnership && partnership.code !== 'velocity' && <StyledOfferPartnershipLabel offer={offer} />}
              <LoadingIndicator floating backdrop="grey-light" opaque visible={!!calendarsLoading} />
              {config.businessTraveller.currentAccountMode === 'business' && pricingToDisplay && <BusinessTravellerOfferCreditsTextLink
                type="estimate"
                offer={offer}
                totalCost={pricingToDisplay.price}
                numberOfNights={pkg.duration}
                roomCount={rooms.length}
              />}
              <PriceRowAgentHubCommission
                size="L"
                productType={offer.productType}
                offerId={offer.id}
                offerLocations={offerLocations}
                vendorName={vendorName}
                packageId={pkg.id}
              />
            </PackagePrices>
          }
          <TextButton
            kind="primary"
            data-testid="OfferAvailable"
            onClick={onViewPackageOptions}
            fit="flex"
          >
            {viewOptionsLabel}
          </TextButton>
          {showDisclaimers && <Group direction="vertical" gap={8}>
            {flightsEnabled && !hideNoCacheMessaging && <Group direction="vertical" horizontalAlign="center">
              {!offer.bundledWithFlightsOnly && hasFlights && <PriceRowFlightPackage
                product="hotel"
                data-testid="FlightPrice"
                price={showMemberPrice ? memberPricingWithFlights?.price : pricingWithFlights.price}
              />}
              {!hasFlights && flightOrigin?.code === flightDestination?.code &&
                <ChangeDepartureWrapper>
                  <PriceRowCaption>
                    No flights available from {flightOrigin.name}. Please select another airport.
                  </PriceRowCaption>
                  <TextLink onClick={onToggleFilter} data-filter="airport">
                    Change departure airport
                  </TextLink>
                </ChangeDepartureWrapper>
              }
              {!hasFlights && flightOrigin?.code !== flightDestination?.code && !departureTooFarInFuture &&
                <PriceRowCaption>
                  {!isIndiaRegion && 'Add flights to the hotel package. Select from all major airlines at the next step.'}
                  {isIndiaRegion && 'Add flights to your hotel package from the best available airlines.'}
                </PriceRowCaption>
              }
              {!hasFlights && departureTooFarInFuture &&
                <ChangeDepartureWrapper>
                  <Caption variant="large" colour="neutral-three">
                    Flights can only be booked up to 11 months in advance.
                  </Caption>
                </ChangeDepartureWrapper>
              }
              <AtolProtectedTooltip />
            </Group>}
            {isBNBLAvailable && !isMemberOnlyOffer && <CaptionTextBlock
              variant="medium"
              startIcon={<LineGiftIcon />}
              align="center"
            >
              Gifting available{' '}
              <SoftOfferPageWalledGarden>
                <Tooltip description={giftingMessage}>
                  <TextLink>Learn more</TextLink>
                </Tooltip>
              </SoftOfferPageWalledGarden>
            </CaptionTextBlock>}
          </Group>}
        </WalledContent>}
      </Group>
      {!availableForPurchase &&
        <>
          <BodyText
            variant="large"
            weight="bold"
            data-testid="OfferSoldOut"
          >
            This offer has SOLD OUT
          </BodyText>
          <TextButton
            kind="primary"
            to={`/${OFFER_TYPE_HOTEL_SLUG}`}
            fit="flex"
          >
            Browse all hotel offers
          </TextButton>
        </>
      }
    </SummaryPane>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  cheapestSelectedOption: getCheapestSelectedPackageOptionWithDefault(appState, state),
  rooms: state.rooms,
  offer: state.offer,
  checkIn: state.checkInDate,
  flightOrigin: state.airport,
  calendarsLoading: arePricesLoading(appState, state),
  isBNBLAvailable: isBNBLAvailable(state),
  isSpoofed: appState.auth.account.isSpoofed,
  durationLabel: getFromDurationLabel(state, appState),
  availableForPurchase: isAvailableForPurchase(state, appState),
}))(FlashOfferSummary)
